import React, { useEffect, useState } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import { axiosFunction } from "../../axiosFunction";
import { formatDateString } from "../../utils/DateFormat";
import Loader from "../../components/Loader/Loader";
import { RedeemPackagesPageContainer } from "../RedeemPackagesPage/RedeemPackagesPageContainer";
import DataTable from "react-data-table-component";
import "./UserRedeemLogs.css";
import { smaartCardUrl } from "../../constant";

const UserRedeemLogs = () => {
  const partnerId = localStorage.getItem("partnerId");

  const [users, setUsers] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  // State and City management
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Fetch states on component mount
  useEffect(() => {
    const getStates = async () => {
      setLoadingStates(true);
      try {
        const stateRes = await axiosFunction(
          `${smaartCardUrl}/location/api/allStates`,
          "GET",
          null
        );
        setStateList(stateRes?.data || []);
      } catch (error) {
        console.error("Failed to fetch states:", error);
        setStateList([]);
      }
      setLoadingStates(false);
    };
    getStates();
  }, []);

  // Fetch cities when state is selected - USING stateCode for city API
  const onStateChange = async (e) => {
    const selectedStateCode = e.target.value;
    setSelectedState(selectedStateCode);
    setSelectedCity(""); // Clear previously selected city
    setCityList([]); // Clear previous city list

    if (!selectedStateCode) {
      return;
    }

    setLoadingCities(true);
    try {
      console.log("Fetching cities for state code:", selectedStateCode);

      const cityRes = await axiosFunction(
        `${smaartCardUrl}/location/api/city/${selectedStateCode}`, // Use stateCode directly
        "GET",
        null
      );

      if (cityRes?.data) {
        setCityList(cityRes.data);
      } else {
        setCityList([]);
      }
    } catch (error) {
      console.error("Failed to fetch cities:", error);
      setCityList([]);
    }
    setLoadingCities(false);
  };

  // Fetch users with pagination and filters
  const fetchUsers = async (page = 1, limit = perPage, search = searchText, city = selectedCity, state = selectedState, cityListData = cityList, stateListData = stateList) => {
    setLoading(true);
    try {
      // Prepare city names array - handle both object and string formats
      let cityNames = [];
      if (city) {
        // If cityList contains objects with cityName property
        if (cityListData.length > 0 && cityListData[0]?.cityName) {
          const cityObj = cityListData.find(c => c.cityId === parseInt(city) || c.cityName === city);
          cityNames = cityObj ? [cityObj.cityName] : [city];
        } else {
          cityNames = [city];
        }
      }

      // Get state name from stateList using stateCode
      let stateName = "";
      if (state) {
        const stateObj = stateListData.find(s => s.stateCode === state);
        stateName = stateObj ? stateObj.stateName : state;
      }
      
      console.log("Selected State Name:", stateName);
      
      const payload = {
        searchValue: search,
        length: limit,
        start: (page - 1) * limit,
        draw: page,
        cityNames: cityNames,
        stateName: stateName,
        partnerId: parseInt(partnerId)  
      };

      console.log("API Payload:", payload);

      const res = await axiosFunction(
        `${smaartCardUrl}/api/v1/dfordr/user/getActiveUsers`,
        "POST",
        payload
      );

      console.log("API Response:", res);

      if (res?.data?.status) {
        let usersData = [];
        let totalRecords = 0;
        
        // Extract data based on API response structure
        if (Array.isArray(res.data.data)) {
          usersData = res.data.data;
          totalRecords = res.data.recordsTotal || res.data.recordsFiltered || usersData.length;
        } else if (Array.isArray(res.data.response)) {
          usersData = res.data.response;
          totalRecords = res.data.recordsTotal || res.data.recordsFiltered || usersData.length;
        } else if (res.data.response && typeof res.data.response === 'object') {
          if (Array.isArray(res.data.response.data)) {
            usersData = res.data.response.data;
            totalRecords = res.data.response.recordsTotal || res.data.response.recordsFiltered || usersData.length;
          } else {
            usersData = Object.values(res.data.response);
            totalRecords = usersData.length;
          }
        } else {
          usersData = [];
          totalRecords = 0;
        }
        
        setUsers(usersData);
        setTotalRows(totalRecords);
      } else {
        console.log("API returned non-success status:", res?.data);
        setUsers([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching active users:", error);
      setUsers([]);
      setTotalRows(0);
    }
    setLoading(false);
  };

  // Export to Excel function
  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      // Prepare city names array
      let cityNames = [];
      if (selectedCity) {
        if (cityList.length > 0 && cityList[0]?.cityName) {
          const cityObj = cityList.find(city => city.cityId === parseInt(selectedCity) || city.cityName === selectedCity);
          cityNames = cityObj ? [cityObj.cityName] : [selectedCity];
        } else {
          cityNames = [selectedCity];
        }
      }

      // Get state name from stateList using stateCode
      let stateName = "";
      if (selectedState) {
        const stateObj = stateList.find(state => state.stateCode === selectedState);
        stateName = stateObj ? stateObj.stateName : selectedState;
      }

      const payload = {
        searchValue: searchText,
        length: totalRows, // Export all records
        start: 0,
        draw: 1,
        cityNames: cityNames,
        stateName: stateName,
        partnerId: parseInt(partnerId) || 81
      };

      console.log("Export API Payload:", payload);

      // Call the export API with blob response type
      const response = await axiosFunction(
        `${smaartCardUrl}/api/v1/dfordr/user/activeUsers/download/excel`,
        "POST",
        payload,
        false, // fileType = false (not multipart/form-data)
        'blob' // responseType = blob for file download
      );

      if (response && response.data) {
        // Create blob from response data
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Create filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.download = `active-users-${timestamp}.xlsx`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);

        console.log("Excel export completed successfully");
      } else {
        throw new Error("No data received from export API");
      }

    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Failed to export data. Please try again.");
    }
    setExportLoading(false);
  };

  // Initial load and when partnerId changes
  useEffect(() => {
    fetchUsers(1, perPage, "");
  }, []);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUsers(page, perPage, searchText, selectedCity, selectedState, cityList, stateList);
  };

  // Handle per page change
  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchUsers(page, newPerPage, searchText, selectedCity, selectedState, cityList, stateList);
  };

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers(1, perPage, searchText, selectedCity, selectedState, cityList, stateList);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  // Handle state filter change
  useEffect(() => {
    if (selectedState !== "") {
      setCurrentPage(1);
      fetchUsers(1, perPage, searchText, selectedCity, selectedState, cityList, stateList);
    }
  }, [selectedState]);

  // Handle city filter change
  useEffect(() => {
    if (selectedCity !== "") {
      setCurrentPage(1);
      fetchUsers(1, perPage, searchText, selectedCity, selectedState, cityList, stateList);
    }
  }, [selectedCity]);

  // Handle date filters
  useEffect(() => {
    if (fromDate || toDate) {
      setCurrentPage(1);
      fetchUsers(1, perPage, searchText, selectedCity, selectedState, cityList, stateList);
    }
  }, [fromDate, toDate]);

  // FIXED: Clear all filters function
  const handleClearFilters = () => {
    // Reset all state variables
    setSearchText("");
    setSelectedState("");
    setSelectedCity("");
    setCityList([]);
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    
    // Fetch users with explicitly empty filters
    fetchUsers(1, perPage, "", "", "", [], stateList);
  };

  const columns = [
    {
      name: "User Name",
      selector: (row) => row?.userName || "-",
      sortable: true,
    },
    {
      name: "Phone Number",
      selector: (row) => row?.phoneNumber || "-",
    },
    {
      name: "Card Number",
      selector: (row) => row?.cardNumber || "-",
    },
    {
      name: "Subscription",
      selector: (row) => row?.subscriptionName || "-",
      wrap: true,
    },
    {
      name: "Start Date",
      selector: (row) => formatDateString(row?.startDate),
      sortable: true,
    },
    {
      name: "End Date",
      selector: (row) => formatDateString(row?.endDate),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row?.status || "Active",
      sortable: true,
      cell: (row) => (
        <span
          className={`badge ${
            row?.status?.toLowerCase() === "active" 
              ? "bg-success" 
              : row?.status?.toLowerCase() === "inactive" 
              ? "bg-danger" 
              : "bg-warning"
          }`}
        >
          {row?.status || "Active"}
        </span>
      ),
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#024089",
        color: "white",
        fontWeight: "bold",
        fontSize: "14px",
      },
    },
    rows: {
      style: {
        fontSize: "14px",
        minHeight: "52px",
      },
    },
    pagination: {
      style: {
        fontSize: "14px",
      },
    },
  };

  return (
    <RedeemPackagesPageContainer>
      <Header />
      <div className="allContent">
        <Container>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Active Users</h5>
            <Button
              variant="success"
              onClick={exportToExcel}
              disabled={exportLoading || users.length === 0}
              className="d-flex align-items-center"
            >
              {exportLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="fas fa-file-excel me-2"></i>
                  Export to Excel
                </>
              )}
            </Button>
          </div>

          <Row className="mb-3 gx-2 gy-2 align-items-center">
            <Col xs={12} sm={12} md={3}>
              <Form.Control
                type="text"
                placeholder="🔍 Search by Name, Phone, Card, Subscription"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>

            <Col xs={6} sm={6} md={2}>
              <Form.Select
                value={selectedState}
                onChange={onStateChange}
                disabled={loadingStates}
              >
                <option value="">All States</option>
                {stateList.map((state) => (
                  <option 
                    key={state.stateCode} 
                    value={state.stateCode}
                  >
                    {state.stateName}
                  </option>
                ))}
              </Form.Select>
              {loadingStates && <small className="text-muted">Loading states...</small>}
            </Col>

            <Col xs={6} sm={6} md={2}>
              <Form.Select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                disabled={!selectedState || loadingCities}
              >
                <option value="">All Cities</option>
                {cityList.map((city) => (
                  <option 
                    key={city.cityId} 
                    value={city.cityId || city.cityName}
                  >
                    {city.cityName}
                  </option>
                ))}
              </Form.Select>
              {loadingCities && <small className="text-muted">Loading cities...</small>}
            </Col>

            <Col xs={12} sm={12} md={1}>
              <button 
                className="btn btn-outline-secondary w-100"
                onClick={handleClearFilters}
                title="Clear all filters"
              >
                Clear
              </button>
            </Col>
          </Row>

          <div className="custom-datatable">
            <DataTable
              columns={columns}
              data={users}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={currentPage}
              paginationPerPage={perPage}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              striped
              highlightOnHover
              responsive
              dense
              noDataComponent={
                <div className="text-center py-4">
                  {loading ? "Loading..." : "No active users found"}
                </div>
              }
              customStyles={customStyles}
            />
          </div>
        </Container>
      </div>
      <Footer />
    </RedeemPackagesPageContainer>
  );
};

export default UserRedeemLogs;